import type { Href } from 'expo-router';

/** `standby_booking_confirmed`'s `payment_method` values (spec TRACKING_TAXONOMY §15). */
export type StandbyBookingConfirmedPaymentMethod =
  | 'card'
  | 'card_on_file'
  | 'wallet'
  | 'in_person'
  | 'gift_card';

/**
 * `drawer`: opened from the in-app entry drawer, so app frame + X, and exits
 * return to the drawer's host. Absent on web, links and the status page.
 */
export type StandbyCheckoutSource = 'drawer';

export const routes = {
  home: '/',
  notFound: '/+not-found',

  signIn: '/auth',
  signInEmail: '/auth/sign-in/email',
  signInOtp: '/auth/sign-in/otp',
  accountDisabled: '/auth/account-disabled',
  signUp: '/auth/sign-up',
  signUpSecondStep: '/auth/sign-up/second-step',
  signUpOtp: '/auth/sign-up/otp',
  signUpAvatar: '/auth/sign-up/avatar',
  signUpConfirmation: '/auth/sign-up/confirmation',

  appointmentSignIn: '/auth/appointment',
  appointmentHome: '/(appointment)',
  appointmentLookUp: '/lookup',
  appointments: '/booking/appointments',
  appointmentDetail: (params: { saleOrderIdOrAppointmentId: string }) =>
    `/booking/appointments/${params.saleOrderIdOrAppointmentId}` as Href,
  appointmentRating: (params: { saleOrderIdOrAppointmentId: string }) =>
    `/booking/appointments/${params.saleOrderIdOrAppointmentId}/rating` as Href,
  appointmentReschedule: (params: { saleOrderIdOrAppointmentId: string }) =>
    `/booking/appointments/${params.saleOrderIdOrAppointmentId}/reschedule` as Href,

  // SQR-16316. `id` is the notification row id the page reads its copy from; `src` is analytics
  // attribution only and is unverified.
  customerNotification: (params: { id: string; src?: string }): Href => ({
    pathname: '/booking/customer-notification/[id]',
    params,
  }),

  profile: '/profile' as Href,

  cards: '/cards' as Href,
  support: '/support' as Href,
  errorTest: '/error-test',

  search: '/search',

  chooseProfessional: (params: {
    shopRoute: string;
    cart?: string;
    promoId?: string;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]',
    params,
  }),
  chooseService: (params: {
    shopRoute: string;
    cart?: string;
    barberId: string;
    promoId?: string;
    isAddedAppointment?: boolean;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]/barber/[barberId]/services',
    params: {
      ...params,
      isAddedAppointment: params.isAddedAppointment ? 'true' : undefined,
    },
  }),
  chooseServiceByCategory: ({
    isAddedAppointment,
    ...params
  }: {
    shopRoute: string;
    barberId: string;
    groupId: string;
    categoryId: string;
    cart?: string;
    promoId?: string;
    isAddedAppointment?: boolean;
  }): Href => ({
    pathname:
      '/booking/book/[shopRoute]/barber/[barberId]/group/[groupId]/[categoryId]',
    params: {
      ...params,
      isAddedAppointment: isAddedAppointment ? 'true' : undefined,
    },
  }),
  chooseServiceCategory: ({
    query,
    ...params
  }: {
    shopRoute: string;
    barberId: string;
    cart?: string;
    promoId?: string;
    isAddedAppointment?: boolean;
    /** Extra query params (e.g. UTM) forwarded untouched. */
    query?: Record<string, string | string[] | undefined>;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]/barber/[barberId]/group',
    params: {
      ...query,
      ...params,
      isAddedAppointment: params.isAddedAppointment ? 'true' : undefined,
    },
  }),
  chooseServiceSubcategory: (params: {
    shopRoute: string;
    barberId: string;
    groupId: string;
    cart?: string;
    promoId?: string;
    isAddedAppointment?: boolean;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]/barber/[barberId]/group/[groupId]',
    params: {
      ...params,
      isAddedAppointment: params.isAddedAppointment ? 'true' : undefined,
    },
  }),
  chooseTime: (params: {
    shopRoute: string;
    barberId: string;
    cart?: string;
    anyTime?: boolean;
    isRebooking?: boolean;
    promoId?: string;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]/barber/[barberId]/schedule',
    params: {
      ...params,
      anyTime: params.anyTime ? 'true' : undefined,
      isRebooking: params.isRebooking ? 'true' : undefined,
    },
  }),
  // Deep-link entry point (e.g. Google AI Mode): scopes straight to a service
  // and, optionally, a specific time slot to preselect — see SQR-15167.
  chooseTimeFromDeepLink: (params: {
    shopRoute: string;
    barberId: string;
    serviceId: string;
    // Named `preselect` per SQR-15167's Jira comment, matching Google's
    // actual query param for this route.
    preselect?: string;
    promoId?: string;
  }): Href => ({
    pathname:
      '/booking/book/[shopRoute]/barber/[barberId]/services/[serviceId]/schedule',
    params,
  }),
  brandLocations: (params: {
    brandRoute: string;
    /** Shop the customer is already booked into — excluded from the list. */
    shopId?: string;
  }): Href => ({
    pathname: '/booking/brands/[brandRoute]',
    params,
  }),
  giftCard: (params: {
    shopId: string;
    promoId?: string;
    barberId?: string;
  }): Href =>
    `/booking/gift-card/${params.shopId}/barber/${params.barberId || 'any'}` as Href,
  viewReservation: (params: { saleOrderId: string; bnpl?: string }): Href => ({
    pathname: '/booking/view-reservation/[saleOrderId]',
    params,
  }),
  bnplReturn: (params: { shopRoute: string; saleOrderId: string }): Href => ({
    pathname: '/booking/book/[shopRoute]/bnpl-return/[saleOrderId]',
    params,
  }),
  cashAppReturn: (params: {
    shopRoute: string;
    saleOrderId: string;
  }): Href => ({
    pathname: '/booking/book/[shopRoute]/cashapp-return/[saleOrderId]',
    params,
  }),
  viewGiftCard: (params: { giftCardId: string }): Href => ({
    pathname: '/booking/view-gift-card/[giftCardId]',
    params,
  }),

  viewWaitlist: (params: { waitlistCustomerId: string }): Href =>
    ({
      pathname: '/booking/waiting-list/[waitlistCustomerId]',
      params,
    }) as Href,

  viewWaitlistWithSlot: (params: {
    waitlistCustomerId: string;
    slotId: string;
  }): Href =>
    ({
      pathname: '/booking/waiting-list/[waitlistCustomerId]',
      params: {
        waitlistCustomerId: params.waitlistCustomerId,
        slotId: params.slotId,
      },
    }) as Href,

  waitlistReschedule: (params: { waitlistCustomerId: string }): Href =>
    ({
      pathname: '/booking/waiting-list/[waitlistCustomerId]/reschedule',
      params,
    }) as Href,

  standbyWaitlistJoined: (params: {
    entryId: string;
    shopRoute: string;
  }): Href => ({
    pathname: '/booking/standby-waitlist/[entryId]',
    params,
  }),

  referral: (params: { shopId: string; customerId: string }): Href => ({
    pathname: '/booking/referral/[shopId]/[customerId]',
    params,
  }),

  campaign: (params: { campaignUrl: string }): Href => ({
    pathname: '/booking/campaign',
    params,
  }),

  standbyIndex: '/booking/standby' as Href,
  standby: (shopId: string, params?: { returning?: boolean }): Href => ({
    pathname: '/booking/standby/[shopId]',
    params: { shopId, ...(params?.returning ? { returning: 'true' } : {}) },
  }),
  // `existing` carries the registration new-vs-existing verdict forward so the
  // summary's join event can report `is_existing_customer` — threaded as a
  // string param (URL-safe, survives deep links) and omitted when unknown.
  standbyStylist: (shopId: string, params?: { existing?: boolean }): Href => ({
    pathname: '/booking/standby/[shopId]/stylist',
    params: {
      shopId,
      ...(params?.existing !== undefined
        ? { existing: String(params.existing) }
        : {}),
    },
  }),
  standbyService: (
    shopId: string,
    params?: { barberId?: string; existing?: boolean },
  ): Href => ({
    pathname: '/booking/standby/[shopId]/service',
    params: {
      shopId,
      ...(params?.barberId ? { barberId: params.barberId } : {}),
      ...(params?.existing !== undefined
        ? { existing: String(params.existing) }
        : {}),
    },
  }),
  standbySummary: (
    shopId: string,
    params?: { barberId?: string; serviceIds?: string; existing?: boolean },
  ): Href => ({
    pathname: '/booking/standby/[shopId]/summary',
    params: {
      shopId,
      ...(params?.barberId ? { barberId: params.barberId } : {}),
      ...(params?.serviceIds ? { serviceIds: params.serviceIds } : {}),
      ...(params?.existing !== undefined
        ? { existing: String(params.existing) }
        : {}),
    },
  }),
  // `joined: true` is set ONLY by the summary's post-join success navigation so
  // the status page fires `standby_confirmation_viewed` once; every other
  // entry (links, re-opens, the duplicate redirect, the active-entry guard)
  // omits it, and the screen strips it on first render so a refresh does not
  // re-fire confirmation (spec Decisions).
  standbyEntry: (
    shopId: string,
    entryId: string,
    params?: { joined?: boolean; src?: string; invite?: string },
  ): Href => ({
    pathname: '/booking/standby/[shopId]/entry/[entryId]',
    params: {
      shopId,
      entryId,
      ...(params?.joined ? { joined: 'true' } : {}),
      ...(params?.src ? { src: params.src } : {}),
      ...(params?.invite ? { invite: params.invite } : {}),
    },
  }),
  standbyEntries: '/booking/standby/entries' as Href,
  standbyCheckout: (
    shopId: string,
    entryId: string,
    params?: { src?: StandbyCheckoutSource },
  ): Href => ({
    pathname: '/booking/standby/[shopId]/entry/[entryId]/checkout',
    params: {
      shopId,
      entryId,
      ...(params?.src ? { src: params.src } : {}),
    },
  }),
  standbyOrderConfirmed: (
    shopId: string,
    entryId: string,
    params: {
      saleOrderId: string;
      /** The precise method submitted to accept — set only right after a fresh accept (spec Decisions). */
      method?: StandbyBookingConfirmedPaymentMethod;
      src?: StandbyCheckoutSource;
    },
  ): Href => ({
    pathname: '/booking/standby/[shopId]/entry/[entryId]/confirmed',
    params: {
      shopId,
      entryId,
      saleOrderId: params.saleOrderId,
      ...(params.method ? { method: params.method } : {}),
      ...(params.src ? { src: params.src } : {}),
    },
  }),

  tabs: {
    home: '/',
  },
} as const;

type AppRoutes = typeof routes;

export type RouteKey = keyof AppRoutes;

export type RouteValue = AppRoutes[RouteKey];
